import { useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { createDataFetchList } from '../../../Chart/dataFetchList';

import { Aggregation, TimeBucket } from '../../../../services/constants';
import { timeRangeToTimeSpan } from '../../../../services/datetime';
import NodeDataSource from '../NodeDataSource';
import { assetsFromNodes } from './SignalSourceFunctions';
import { getColors } from 'utils/colors';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';
import { useAssetQueries } from 'components/Asset/queries';
import { useTimeseriesQuery } from 'services/TelemetryApi/queries';

const DataPointsType = PropTypes.shape([PropTypes.number.isRequired, PropTypes.number.isRequired]);

const SignalDataType = PropTypes.shape({
  aggregate: PropTypes.string.isRequired,
  assetDisplayName: PropTypes.string.isRequired,
  assetId: PropTypes.string.isRequired,
  assetPath: PropTypes.string,
  dataPoints: PropTypes.arrayOf(DataPointsType),
  decimalPoint: PropTypes.number,
  signalDisplayName: PropTypes.string.isRequired,
  signalId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  unit: PropTypes.string
});


const createSignalsQuery = ({ assetId, modelId, signals, aggregate, displayName, interval, showPath }) => {
  const s = _.reduce(signals, (result, { name, displayName: signalDisplayName, schema, modelId: signalModelId, unit }) => {
    // Add only signals that matches the models telemetry
    if (modelId === signalModelId) {
      result.push({
        name,
        type: schema,
        aggregate: (interval !== TimeBucket.RAW) ? aggregate : null,
        fullName: (showPath) ? `${displayName} > ${signalDisplayName}` : signalDisplayName,
        displayName: signalDisplayName,
        unit: unit
      });
    }
    return result;
  }, []);
  return {
    assetId,
    displayName,
    signals: s
  };
};

/**
 * TODO: Update with proper types when available
 * @typedef {Object} AssetFromSignalsProp
 * @property {Object[]} nodes
 * @property {string[]} modelIds
 */

const emptyArray = [];
/**
 * @param {AssetFromSignalsProp} props
 * @returns {AssetType[]} Assets that have one or more of the modelIds
 */
export const useAssetsFromModelIds = ({ nodes, modelIds }) => {

  const hasModelIds = useMemo(() => _.some(assetsFromNodes(nodes), ({ modelId, details }) => modelId != null || details?.modelId != null),
    [nodes]);

  const assetIds = useMemo(() => {
    const assets = _.map(assetsFromNodes(nodes), 'id');
    // TODO: Temp fix for not hammering backend with all assets
    // TODO: Remove this, once there is a better way to handle this
    return _.take(assets, 10);
  }, [nodes]);

  const { assets } = useAssetQueries(hasModelIds ? emptyArray : assetIds);

  return useMemo(() => {
    if (hasModelIds) {
      return _.filter(assetsFromNodes(nodes), ({ modelId, details }) => _.includes(modelIds, modelId ?? details?.modelId));
    }
    // Fallback to old implementation of fetching data from new assets
    return _.filter(assets, ({ modelId, details }) => _.includes(modelIds, modelId ?? details?.modelId));
  }, [assets, hasModelIds, modelIds, nodes]);
};

const SignalFunction =
  ({
    nodes: nodesFromDatasource,
    signals = [],
    timeRange = null,
    aggregate = Aggregation.MEAN,
    interval = TimeBucket.PT1H,
    reloadTrigger,
    dashboardDate,
    showPath = true
  }) => {
    const { nodes, isLoading: isLoadingNodes } = NodeDataSource({ nodes: nodesFromDatasource });

    const modelIds = useMemo(() => _.map(signals, 'modelId'), [signals]);
    const assets = useAssetsFromModelIds({ nodes, modelIds });

    const { convertToUserPairArray } = useMeasurementSystem();
    const signalSets = useMemo(() => {
      if (_.isEmpty(signals) || _.isEmpty(assets)) {
        return;
      }
      const queries = _.map(assets, ({ id, modelId, displayName, details }) => {
        // TODO: Update with correct modelId location when all apis have modelId
        const realModelId = modelId ?? details?.modelId;
        return createSignalsQuery({ assetId: id, modelId: realModelId, signals, aggregate, displayName, interval, showPath });
      });
      return _.reject(queries, query => _.isEmpty(query.signals));
    }, [signals, assets, aggregate, interval, showPath]);

    const _timespan = useMemo(() => {
      _.noop(reloadTrigger); // stops complaining about not used variable, without adding eslint block
      return timeRangeToTimeSpan(timeRange, dashboardDate());
    }, [reloadTrigger, timeRange, dashboardDate]);

    const isTelemetryEnabled = !_.isEmpty(signalSets) && !_.isEmpty(_timespan) && interval != null && !isLoadingNodes;

    const telemetryQuery = useTimeseriesQuery([signalSets, reloadTrigger],
      createDataFetchList(signalSets),
      _timespan,
      interval,
      isTelemetryEnabled);

    const colors = useMemo(() => {
      if (_.isEmpty(telemetryQuery.data)) {
        return [];
      }

      return getColors({ usedColors: [], numColors: _.size(telemetryQuery.data) });
    }, [telemetryQuery.data]);

    const signalData = useMemo(() => {
      if (_.isEmpty(signalSets) || _.isEmpty(telemetryQuery.data)) {
        return [];
      }
      const consolidated = _.map(telemetryQuery.data, (dataSignal, index) => {
        const asset = _.first(_.filter(signalSets, ['assetId', dataSignal.assetId]));
        const signalSchema = _.first(_.filter(asset.signals, ['name', dataSignal.name]));
        // Convert the data to the correct unit
        const [dataPoints, schemaUnit] = convertToUserPairArray({ values: dataSignal.data, schemaUnit: { schema: signalSchema.type, unit: signalSchema.unit } });
        return {
          assetId: dataSignal.assetId,
          assetDisplayName: asset.displayName,
          assetPath: `${asset.displayName} > ${signalSchema.displayName}`,
          signalId: dataSignal.name,
          signalDisplayName: signalSchema.displayName,
          color: colors[index],
          type: schemaUnit.schema,
          unit: schemaUnit.unit,
          aggregate: signalSchema.aggregate,
          decimalPoint: dataSignal.decimalPoint,
          dataPoints: dataPoints,
          // TODO: This can probably be better. Right now we just add a modified dataSignal, that we get in the graphDataPackage
          updatedTelemetry: { ...dataSignal, data: dataPoints }
        };
      });
      return consolidated;
    }, [colors, convertToUserPairArray, signalSets, telemetryQuery.data]);

    const graphDataPackage = useMemo(() => {
      if (_.isEmpty(signalSets) ||
        _.isEmpty(_timespan) ||
        interval == null) {
        return null;
      }
      if (signalData) {
        return {
          data: _.map(signalData, 'updatedTelemetry'),
          signalSets,
          colors
        };
      }
    }, [_timespan, colors, interval, signalData, signalSets]);

    const hasError = telemetryQuery.isError;
    const isLoadingTelemetry = telemetryQuery.isLoading && isTelemetryEnabled;
    return useMemo(() => ({
      hasError,
      isLoading: isLoadingTelemetry || isLoadingNodes,
      timespan: _timespan,
      interval,
      data: graphDataPackage,
      signalData: signalData
    }), [hasError, isLoadingTelemetry, isLoadingNodes, _timespan, interval, graphDataPackage, signalData]);
  };

SignalDataType.PropTypes = {
  signalData: PropTypes.arrayOf(SignalDataType)
};

export default SignalFunction;
